/*
 * The shape both of the console's dead ends take: the address that is not
 * there, and the screen that is there and is not yours.
 *
 * They are one component and two screens, which is the right way round. The
 * layout, the register and the promise that there is always a way back are the
 * same; the words are the entire difference, and collapsing the words as well
 * is precisely the mistake `NotFound` and `NoAccess` exist to avoid. Somebody
 * who is told "something went wrong" when they lack a permission spends ten
 * minutes checking a URL that was correct, and somebody told "you do not have
 * access" to an address that never existed asks an administrator for a
 * permission that would not have helped.
 *
 * There is no wordmark here, unlike the public screens. Both of these render
 * inside the signed-in frame, which carries the brand in the sidebar already,
 * and a second one in the content region would read as a page that had failed
 * to load the application around it.
 *
 * The headline is the page's real `h1`. `data/EmptyState` was the obvious
 * component to reach for and is deliberately not used: it renders its headline
 * as a paragraph, on the stated grounds that an empty list is not a section of
 * a page, and that reasoning is exactly inverted here. This *is* the page, and
 * a screen whose only heading is invisible to the document outline cannot be
 * navigated to by the one key a screen reader user presses first.
 */

import { useId } from 'react'
import type { ReactNode } from 'react'
import styles from './DeadEnd.module.css'

export interface DeadEndProps {
  title: string
  /** The explanation. One or two short paragraphs, never a shrug. */
  children: ReactNode
  /**
   * The specifics: the address that was tried, the role that was held. Set
   * apart from the prose because they are the part somebody copies into a
   * message to whoever can fix it.
   */
  facts?: ReactNode
  /** The way back, and anything more useful standing beside it. */
  actions?: ReactNode
}

export function DeadEnd({ title, children, facts, actions }: DeadEndProps) {
  const titleId = useId()

  return (
    <section className={styles.deadEnd} aria-labelledby={titleId}>
      <h1 className={styles.title} id={titleId}>
        {title}
      </h1>

      <div className={styles.body}>{children}</div>

      {facts ? <div className={styles.facts}>{facts}</div> : null}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </section>
  )
}
